import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { historyVariables } from '../lib/records'
import type { CostAttachmentTarget, CostRfq, CostRfqAward } from '../types'
import { WRITE, useCostWriteQueue, type Mutate, type Reload, type Vars } from './useCostWriteQueue'

/**
 * RFQ, quote and RFQ-attachment writes (part D spec §5). A write reloads the RFQ's detail and history, also after a
 * refusal; `onDetailError` hears when the detail reload itself fails (the RFQ may be gone).
 */
export function useRfqMutations(projectId: string, onDetailError?: (rfqId: string, error: unknown) => void) {
  const { saving, run } = useCostWriteQueue()
  const [create] = useMutation(Mutation.CREATE_COST_RFQ, WRITE)
  const [update] = useMutation(Mutation.UPDATE_COST_RFQ, WRITE)
  const [remove] = useMutation(Mutation.DELETE_COST_RFQ, WRITE)
  const [addQuote] = useMutation(Mutation.ADD_COST_RFQ_QUOTE, WRITE)
  const [updateQuote] = useMutation(Mutation.UPDATE_COST_RFQ_QUOTE, WRITE)
  const [removeQuote] = useMutation(Mutation.DELETE_COST_RFQ_QUOTE, WRITE)
  const [award] = useMutation(Mutation.AWARD_COST_RFQ, WRITE)
  const [revoke] = useMutation(Mutation.REVOKE_COST_RFQ_AWARD, WRITE)
  const [cancel] = useMutation(Mutation.CANCEL_COST_RFQ, WRITE)
  const [reopen] = useMutation(Mutation.REOPEN_COST_RFQ, WRITE)
  const [addAttachment] = useMutation(Mutation.ADD_COST_ATTACHMENT, WRITE)
  const [removeAttachment] = useMutation(Mutation.REMOVE_COST_ATTACHMENT, WRITE)
  const detail = (id: string): Reload[] => [
    { query: Query.COST_RFQ, variables: { id }, onError: onDetailError && ((e: unknown) => onDetailError(id, e)) },
    { query: Query.COST_ACTIVITY_LOG, variables: historyVariables(projectId, 'RFQ', id) },
  ]
  const register: Reload[] = [{ query: Query.COST_RFQS, variables: { projectId, filter: {} } }]
  const m = (fn: unknown) => fn as Mutate

  return {
    saving,
    create: (input: Vars) => run<{ createCostRfq: CostRfq }>(m(create), { projectId, input }, register),
    update: (id: string, input: Vars) => run(m(update), { id, input }, detail(id)),
    remove: (id: string) => run(m(remove), { id }, register, detail(id)),
    addQuote: (rfqId: string, input: Vars) => run(m(addQuote), { rfqId, input }, detail(rfqId)),
    updateQuote: (rfqId: string, id: string, input: Vars) => run(m(updateQuote), { id, input }, detail(rfqId)),
    removeQuote: (rfqId: string, id: string) => run(m(removeQuote), { id }, detail(rfqId)),
    /** Spec §4: the page opens the result next; its own screen loads it there. */
    award: (rfqId: string, quoteId: string) => run<{ awardCostRfq: CostRfqAward }>(m(award), { quoteId }, detail(rfqId)),
    revoke: (rfqId: string) => run(m(revoke), { rfqId }, detail(rfqId)),
    cancel: (id: string) => run(m(cancel), { id }, detail(id)),
    reopen: (id: string) => run(m(reopen), { id }, detail(id)),
    addAttachment: (rfqId: string, target: CostAttachmentTarget, source: { fileId: string } | { docRevisionId: string }) =>
      run(m(addAttachment), { target, ...source }, detail(rfqId)),
    removeAttachment: (rfqId: string, attachmentId: string) => run(m(removeAttachment), { id: attachmentId }, detail(rfqId)),
  }
}

export type RfqMutations = ReturnType<typeof useRfqMutations>
